"use client";

import { DesktopMenu } from "@/components/header/desktop-menu";
import { MobileProfileMenu } from "@/components/header/mobile-profile-menu";
import { Logo } from "@/components/rac/logo";
import { useAuth } from "@/hooks/use-auth";
import { useSellerStoreData } from "@/providers/global";
import { trackEvent } from "@/utils/analytics/track-event";
import { trackNavItem } from "@/utils/analytics/track-navigation";
import { Plus } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { MenuItem } from "../homepage/types";

const getMenuItems = ({ currentPath: _, isAuthenticated }: { currentPath: string; isAuthenticated: boolean }) => {
	const baseItems: MenuItem[] = [
		{ label: "Search", href: isAuthenticated ? "/preference" : "/bengaluru" },
		{ label: "Pricing And Services", href: "/pricing" },
		{ label: "Blog", href: "/blog" },
		// {
		// 	label: "Old Website",
		// 	href: `https://web.settlin.in${currentPath}`,
		// 	target: "_blank",
		// 	rel: "noopener noreferrer",
		// },
	];

	return baseItems;
};

const getExtraMenuItems = ({ relation, hasProperties }: { relation?: string; hasProperties: boolean }) => {
	const extraMenuItems: MenuItem[] = [];

	if (relation === "seller" && hasProperties) {
		// Show Dashboard for sellers who have properties (hasProperties = true)
		// This means the seller has already posted properties and can manage them
		extraMenuItems.push({
			label: "Dashboard",
			href: "/dashboard/seller",
		});
	} else if (relation === "buyer") {
		// Show Dashboard for buyers regardless of properties
		// Buyers always get access to their dashboard to manage preferences and saved properties
		extraMenuItems.push({
			label: "Dashboard",
			href: "/dashboard/buyer",
		});
	} else {
		// Show "Post Property Free" in all other cases:
		// 1. User is not logged in (relation is undefined/null)
		// 2. User is logged in as seller but has no properties (hasProperties = false)
		// This encourages property posting for new/empty sellers and non-authenticated users
		extraMenuItems.push({
			icon: Plus,
			label: "Post Property Free",
			href: "/sell",
		});
	}

	return extraMenuItems;
};

export const Header = () => {
	const [isScrolled, setIsScrolled] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const [isFirefox, setIsFirefox] = useState(false);
	const lastScrollY = useRef(0);
	const { isAuthenticated, user } = useAuth();
	const { hasRawUnitAndProperties = false } = useSellerStoreData(state => state);
	const pathname = usePathname();

	// not using searchParams as it will cause a client component adn will need a suspense boundary
	const menuItems = getMenuItems({ currentPath: pathname || "", isAuthenticated });
	const extraMenuItems = getExtraMenuItems({
		relation: user?.relation,
		hasProperties: hasRawUnitAndProperties,
	});

	useEffect(() => {
		const handleScroll = () => {
			const currentScrollY = window.scrollY;

			// Set scrolled state for styling
			setIsScrolled(currentScrollY > 10);

			// Add a small threshold to prevent jittery behavior
			const scrollThreshold = 10;
			const scrollDifference = currentScrollY - lastScrollY.current;

			// Show/hide header based on scroll direction
			if (currentScrollY < 50) {
				// Always show header near the top
				setIsVisible(true);
			} else if (scrollDifference > scrollThreshold && currentScrollY > 100) {
				// Scrolling down - hide header (only after scrolling past 100px)
				setIsVisible(false);
			} else if (scrollDifference < -scrollThreshold) {
				// Scrolling up - show header immediately
				setIsVisible(true);
			}

			lastScrollY.current = currentScrollY;
		};

		// Detect Firefox
		setIsFirefox(navigator.userAgent.toLowerCase().includes("firefox"));

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const itemsWithActive = menuItems.map(item => ({ ...item, active: pathname === item.href }));

	// Firefox-specific header classes to avoid backdrop-blur issues
	const getHeaderClasses = () => {
		const baseClasses = "fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out";
		const visibilityClasses = isVisible ? "translate-y-0" : "-translate-y-full";

		if (isScrolled) {
			return isFirefox
				? `${baseClasses} ${visibilityClasses} bg-white border-b border-gray-200/70 shadow-md`
				: `${baseClasses} ${visibilityClasses} bg-white/80 backdrop-blur-md border-b border-gray-200/70 shadow-md`;
		}

		return `${baseClasses} ${visibilityClasses} bg-white border-b border-gray-200 shadow-subtle`;
	};

	const handleLogoClick = () => {
		// Track logo navigation
		trackNavItem({ action: "logo", category: "navigation" });

		// Track logo click navigation
		trackEvent({
			google: {
				event: "select_content",
				options: {
					content_type: "navigation",
					item_id: "logo-home",
				},
			},
			matomo: {
				event: "logo-click",
				options: {
					category: "navigation",
				},
			},
		});
	};

	return (
		<header className={getHeaderClasses()}>
			<nav className="max-w-7xl mx-auto px-4">
				<div className="flex justify-between items-center h-16 md:h-20">
					{/* Logo Section */}
					<div className="flex items-center h-full">
						<Link
							href="/"
							className="flex items-center gap-2 hover:opacity-80 transition-opacity no-underline hover:no-underline h-full"
							onClick={handleLogoClick}
						>
							<div className="flex items-center h-full">
								<Logo className="h-10 w-auto text-primary-700" />
								<span className="text-xl md:text-2xl font-bold text-primary-700 tracking-tight font-heading no-underline ml-2">
									SETTLIN
								</span>
							</div>
						</Link>
					</div>

					{/* Desktop Navigation - Only show on large screens */}
					<div className="hidden lg:flex items-center h-full">
						<div className="flex items-center h-full">
							<DesktopMenu menuItems={itemsWithActive} extraMenuItems={extraMenuItems} />
						</div>
					</div>

					{/* Mobile Menu Toggle - Show on small and medium (tablet) screens */}
					<div className="lg:hidden">
						<MobileProfileMenu />
					</div>
				</div>
			</nav>
		</header>
	);
};
